import React from "react";
import { Link } from "react-router-dom";
import { Zap, Fuel, ShieldCheck, ArrowRight, CheckCircle2, Sun, Monitor, Cpu, Snowflake, GraduationCap, Building2 } from "lucide-react";
import { useMitcidco } from "./MitcidcoLayout";
import { TRADES, FEATURES, UPCOMING_COURSES } from "./_shared";

const upcomingIcon = (n) => ({
  sun: <Sun size={22} strokeWidth={1.6} />,
  monitor: <Monitor size={22} strokeWidth={1.6} />,
  cpu: <Cpu size={22} strokeWidth={1.6} />,
  snowflake: <Snowflake size={22} strokeWidth={1.6} />,
}[n] || <Sun size={22} />);

const TradeIcon = ({ k, size = 28 }) => (k === "electrician" ? <Zap size={size} strokeWidth={1.6} /> : <Fuel size={size} strokeWidth={1.6} />);

export default function Home() {
  const data = useMitcidco();
  const inst = data?.institute || {};

  return (
    <div data-testid="mitcidco-home">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#7C2D12] via-[#8A2F0E] to-[#5B1A0A] text-white">
        <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 25% 25%, #D4A017 0, transparent 40%), radial-gradient(circle at 75% 75%, #F97316 0, transparent 40%)" }} />
        <div className="relative max-w-7xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold text-[#D4A017] bg-[#D4A017]/10 border border-[#D4A017]/30 px-3 py-1.5 rounded-full">
              DVET · DGT · NCVT Affiliated · Est. 2017
            </div>
            <h1 className="mt-5 font-serif font-black text-4xl md:text-6xl leading-[1.05]">
              Precision skills.<br /><span className="text-[#D4A017]">Purposeful</span> careers.
            </h1>
            <p className="mt-5 text-white/85 text-lg max-w-xl leading-relaxed">
              A Gramaudyogik Shikshan Mandal institution located inside the MIT Campus, CIDCO N-4, Chhatrapati Sambhajinagar. Over <span className="text-[#D4A017] font-semibold">600 alumni</span> serve top companies and government establishments today.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="trades" className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B58007] text-[#5B1A0A] font-bold text-sm uppercase tracking-wider px-6 py-3.5 shadow-lg shadow-[#D4A017]/20 transition-all hover:-translate-y-0.5" data-testid="mitcidco-hero-explore-trades">
                Explore Trades <ArrowRight size={16} />
              </Link>
              <Link to="contact" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 transition-all" data-testid="mitcidco-hero-contact">
                Visit / Contact
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg">
              <Stat kpi="600+" label="Alumni" />
              <Stat kpi="2" label="NCVT Trades" />
              <Stat kpi="4" label="Upcoming Courses" />
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-white/5 border border-white/10 shadow-2xl">
              <img src={inst.logo_url || "/assets/mitcsn-logo.png"} alt={inst.name} className="w-full h-full object-contain p-16 md:p-20 bg-gradient-to-br from-white to-[#FDF6E3]" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white text-[#7C2D12] p-4 shadow-xl border-l-4 border-[#D4A017] hidden md:block">
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A017] font-bold">DVET Code</div>
              <div className="font-mono font-black text-lg mt-0.5">272515101501</div>
              <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-0.5">NCVT MIS · PU27000880</div>
            </div>
          </div>
        </div>
      </section>

      {/* About strip */}
      <section className="bg-[#FDF6E3] border-y border-amber-100">
        <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold md:pt-2">About</div>
          <div>
            <h2 className="font-serif text-2xl md:text-3xl text-[#7C2D12] leading-snug">
              Serving Chhatrapati Sambhajinagar since <span className="text-[#B45309]">2017</span> — well-equipped, all-inclusive vocational training.
            </h2>
            <p className="mt-5 text-slate-800 leading-relaxed">
              Gramaudyogik Shikshan Mandal established this institute in 2017 to serve the industry-education aspirations of rural youth. Located inside the MIT Campus, CIDCO N-4, our institute is a well-equipped Industrial Training Institute delivering NCVT-accredited certification with a Government-ITI-parallel curriculum.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Badge label="DVET · Govt. of Maharashtra" />
              <Badge label="DGT · NCVT New Delhi" />
              <Badge label="G.S. Mandal Institution" />
            </div>
          </div>
        </div>
      </section>

      {/* Trades grid */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">Current NCVT Trades</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#7C2D12] mt-2">Two industry-aligned trades</h2>
          </div>
          <Link to="trades" className="text-sm font-semibold text-[#7C2D12] hover:text-[#D4A017] inline-flex items-center gap-1">See full curriculum <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
          {TRADES.map((t) => (
            <Link key={t.key} to={`trades#${t.key}`} className="group bg-white border border-amber-100 hover:border-[#D4A017] hover:-translate-y-1 transition-all p-8 relative overflow-hidden" data-testid={`mitcidco-home-trade-${t.key}`}>
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-10 group-hover:opacity-20 transition-opacity" style={{ background: t.accent }} />
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: t.accent }}>
                <TradeIcon k={t.key} size={30} />
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold mt-5" style={{ color: t.accent }}>{t.duration} · NCVT</div>
              <div className="font-serif text-2xl text-[#7C2D12] mt-1.5 leading-tight">{t.nameEn}</div>
              <div className="text-[12px] text-slate-500 mt-1 italic">{t.nameMr}</div>
              <div className="mt-5 flex items-center justify-between text-sm text-slate-700 border-t border-amber-100 pt-4">
                <div><span className="font-bold text-[#7C2D12]">Units</span> · {t.units}</div>
                <div><span className="font-bold text-[#7C2D12]">Seats</span> · {t.intake}</div>
                <div><span className="font-bold text-[#7C2D12]">Elig.</span> · {t.qualification}</div>
              </div>
              <div className="mt-5 text-[12px] font-bold text-[#B45309] inline-flex items-center gap-1 group-hover:gap-2 transition-all">View curriculum <ArrowRight size={12} /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* Upcoming courses */}
      <section className="bg-gradient-to-r from-[#7C2D12] to-[#5B1A0A] text-white">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4A017] font-bold">Upcoming Courses · आगामी अभ्यासक्रम</div>
          <h2 className="font-serif text-3xl md:text-4xl mt-2">Expanding the curriculum next year</h2>
          <p className="mt-3 text-white/80 max-w-2xl text-sm">We&apos;re preparing to launch four new NCVT programmes in the coming academic year — each aligned with sunrise industry demand.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {UPCOMING_COURSES.map((c, i) => (
              <div key={c.title} className="bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors" data-testid={`mitcidco-upcoming-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#D4A017]/15 text-[#D4A017] flex items-center justify-center">{upcomingIcon(c.icon)}</div>
                <div className="mt-4 text-[10px] tracking-[0.2em] uppercase text-[#D4A017] font-bold">Coming Soon</div>
                <div className="mt-1 font-serif text-lg leading-tight">{c.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#B45309] font-bold">Why Marathwada ITI CIDCO</div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2 text-[#7C2D12]">Seven reasons parents &amp; students trust us</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-x-8 gap-y-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-amber-100 p-4">
              <CheckCircle2 className="text-[#D4A017] mt-0.5 shrink-0" size={20} />
              <div className="text-sm text-slate-800 leading-relaxed">{f}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="bg-gradient-to-br from-[#D4A017] to-[#B58007] text-[#5B1A0A] p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/20 blur-2xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase font-bold">Admissions Open</div>
              <h3 className="font-serif text-2xl md:text-4xl mt-2 leading-tight">Ready to start a career with real, industry-ready skills?</h3>
              <p className="mt-3 max-w-xl">Speak to our admissions cell about current intake, fee structure and eligibility. Walk in any working day between 10 am and 5 pm.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="contact" className="inline-flex items-center gap-2 bg-[#7C2D12] hover:bg-[#5B1A0A] text-white font-bold text-sm uppercase tracking-wider px-6 py-3.5 hover:-translate-y-0.5 transition-all"><Building2 size={16} /> Visit Campus</Link>
              <Link to="trades" className="inline-flex items-center gap-2 border-2 border-[#7C2D12] text-[#7C2D12] font-bold text-sm uppercase tracking-wider px-6 py-3.5 hover:bg-[#7C2D12] hover:text-white transition-all"><GraduationCap size={16} /> Choose a Trade</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div>
      <div className="font-serif font-black text-3xl text-[#D4A017]">{kpi}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 mt-1">{label}</div>
    </div>
  );
}

function Badge({ label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-white border border-amber-200 text-[#7C2D12] px-3 py-1.5">
      <ShieldCheck size={12} className="text-[#D4A017]" />{label}
    </span>
  );
}
